/**
 * CountrySelector Component
 * Modern searchable country dropdown for phone input
 *
 * Features:
 * - Search by country name or dialing code
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Flag emoji display
 * - Accessible with ARIA labels
 */

import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { getCountryCallingCode, type CountryCode } from 'libphonenumber-js';
import { cn } from '../../../../../lib/utils';
import { getCountryName, COUNTRY_PHONE_INFO } from '../utils/countryMetadata';

/**
 * Country data for dropdown
 */
interface CountryOption {
  code: CountryCode;
  name: string;
  dialCode: string;
  flag: string;
}

/**
 * Props for CountrySelector
 */
interface CountrySelectorProps {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Convert country code to flag emoji
 */
function countryToFlag(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

/**
 * Get dialing code for country
 */
function getDialCode(countryCode: CountryCode): string {
  try {
    return `+${getCountryCallingCode(countryCode)}`;
  } catch {
    return '';
  }
}

/**
 * Build list of all countries with metadata
 */
function buildCountryList(): CountryOption[] {
  const countries: CountryOption[] = [];

  // Get all countries from our metadata
  const countryCodes = Object.keys(COUNTRY_PHONE_INFO) as CountryCode[];

  for (const code of countryCodes) {
    const dialCode = getDialCode(code);
    if (dialCode) {
      countries.push({
        code,
        name: getCountryName(code),
        dialCode,
        flag: countryToFlag(code),
      });
    }
  }

  // Sort alphabetically by name
  return countries.sort((a, b) => a.name.localeCompare(b.name));
}

// Memoized country list
const ALL_COUNTRIES = buildCountryList();

/**
 * CountrySelector Component
 */
export const CountrySelector: React.FC<CountrySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Get current country data
  const selectedCountry = useMemo(() => {
    return ALL_COUNTRIES.find((c) => c.code === value) || ALL_COUNTRIES[0];
  }, [value]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!search.trim()) return ALL_COUNTRIES;

    const searchLower = search.toLowerCase().trim();
    const searchDigits = search.replace(/\D/g, '');

    return ALL_COUNTRIES.filter((country) => {
      // Match by name
      if (country.name.toLowerCase().includes(searchLower)) return true;
      // Match by country code
      if (country.code.toLowerCase().includes(searchLower)) return true;
      // Match by dialing code (e.g., "62" matches Indonesia +62)
      if (searchDigits && country.dialCode.includes(searchDigits)) return true;
      return false;
    });
  }, [search]);

  /**
   * Handle click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  /**
   * Focus search input when dropdown opens
   */
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  /**
   * Reset highlighted index when filter changes
   */
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredCountries]);

  /**
   * Scroll highlighted item into view
   */
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedElement = listRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredCountries.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCountries[highlightedIndex]) {
            handleSelect(filteredCountries[highlightedIndex].code);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setSearch('');
          break;
        case 'Tab':
          setIsOpen(false);
          setSearch('');
          break;
      }
    },
    [isOpen, filteredCountries, highlightedIndex]
  );

  /**
   * Handle country selection
   */
  const handleSelect = useCallback(
    (countryCode: CountryCode) => {
      onChange(countryCode);
      setIsOpen(false);
      setSearch('');
    },
    [onChange]
  );

  /**
   * Toggle dropdown
   */
  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      if (isOpen) {
        setSearch('');
      }
    }
  }, [disabled, isOpen]);

  return (
    <div
      ref={containerRef}
      className={cn('relative', className)}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Select country, current: ${selectedCountry.name}`}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
          'hover:bg-gray-50 dark:hover:bg-gray-700',
          'focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className="text-xl leading-none">{selectedCountry.flag}</span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown - Using Portal-like fixed positioning with explicit inline styles */}
      {isOpen && (
        <div
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden"
          style={{
            position: 'fixed',
            zIndex: 9999,
            width: 340,
            minWidth: 340,
            maxWidth: 'calc(100vw - 32px)',
            boxSizing: 'border-box',
            top: containerRef.current
              ? containerRef.current.getBoundingClientRect().bottom + 8
              : 0,
            left: containerRef.current
              ? Math.max(16, containerRef.current.getBoundingClientRect().left)
              : 0,
          }}
          role="listbox"
          aria-label="Select country"
        >
          {/* Search Input */}
          <div className="p-4 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by country name or dial code..."
                className={cn(
                  'w-full pl-10 pr-4 py-2.5',
                  'text-sm text-gray-900 dark:text-gray-100',
                  'bg-gray-50 dark:bg-gray-700/50',
                  'border border-gray-200 dark:border-gray-600',
                  'rounded-lg',
                  'placeholder:text-gray-400 dark:placeholder:text-gray-500',
                  'focus:outline-hidden focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500',
                  'transition-all duration-200'
                )}
                aria-label="Search countries"
              />
            </div>
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Type country name or dial code (e.g., "62" for Indonesia)
            </p>
          </div>

          {/* Country List */}
          <div
            ref={listRef}
            className="max-h-72 overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
          >
            {filteredCountries.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <div className="text-gray-400 dark:text-gray-500 mb-1">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  No countries found
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Try a different search term
                </p>
              </div>
            ) : (
              <div className="py-2">
                {filteredCountries.map((country, index) => {
                  const isSelected = country.code === value;
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <button
                      key={country.code}
                      type="button"
                      data-index={index}
                      onClick={() => handleSelect(country.code)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-3',
                        'text-left transition-all duration-150',
                        'hover:bg-gray-50 dark:hover:bg-gray-700/50',
                        isHighlighted && 'bg-gray-50 dark:bg-gray-700/50',
                        isSelected && 'bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                      )}
                    >
                      {/* Flag */}
                      <span className="text-2xl leading-none shrink-0">
                        {country.flag}
                      </span>

                      {/* Country Name & Code */}
                      <div className="flex-1 min-w-0">
                        <span
                          className={cn(
                            'block text-sm truncate',
                            isSelected
                              ? 'text-emerald-700 dark:text-emerald-400 font-semibold'
                              : 'text-gray-800 dark:text-gray-200 font-medium'
                          )}
                        >
                          {country.name}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {country.code}
                        </span>
                      </div>

                      {/* Dial Code */}
                      <span
                        className={cn(
                          'text-sm font-mono shrink-0 px-2 py-1 rounded-md',
                          isSelected
                            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        )}
                      >
                        {country.dialCode}
                      </span>

                      {/* Check mark for selected */}
                      {isSelected && (
                        <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
